// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { logChannelStatus } from "../../lib/realtimeStatus";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

// Icon name per notification type. Falls back to "bell" in the UI.
const NOTIF_ICONS = {
  order: "package",
  payment: "check",
  payout: "banknote",
  message: "message-square",
  admin_message: "message-square",
  promo: "tag",
  review: "star",
  system: "bell",
};

// Route notification taps to the top-level destination. Notifications live
// under Profile, so we call setActive() to jump out to the matching tab.
// Seller role maps to seller-specific tabs (sorders/smessages).
function routeFor(type, isSeller) {
  switch (type) {
    case "order":
    case "payment":
    case "payout":
      return isSeller ? "sorders" : "orders";
    case "message":
    case "admin_message":
      return isSeller ? "smessages" : "messages";
    default:
      return null;
  }
}

export function NotificationsPanel({ user, isSeller, setActive }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    async function load() {
      const { data } = await supabase
        .from("notifications")
        .select("id, type, title, body, data, read_at, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(30);
      if (cancelled) return;
      setItems(data || []);
      setLoading(false);
    }
    load();

    // Live updates — new notifications prepend as they arrive.
    const channelName = `notifications:${user.id}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (cancelled) return;
          setItems((prev) => [payload.new, ...prev]);
        },
      )
      .subscribe(logChannelStatus(channelName));

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  async function markRead(id) {
    const ts = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: ts } : n)));
    await supabase.from("notifications").update({ read_at: ts }).eq("id", id);
  }

  async function markAllRead() {
    const unreadIds = items.filter((n) => !n.read_at).map((n) => n.id);
    if (!unreadIds.length) return;
    const ts = new Date().toISOString();
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || ts })));
    await supabase.from("notifications").update({ read_at: ts }).in("id", unreadIds);
  }

  function handleTap(n) {
    if (!n.read_at) markRead(n.id);
    const dest = routeFor(n.type, isSeller);
    if (dest && setActive) setActive(dest);
  }

  const unreadCount = items.filter((n) => !n.read_at).length;

  return (
    <Card pad={0}>
      <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.cream}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <Eyebrow>Notifications</Eyebrow>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>{unreadCount} unread</div>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            style={{ all: "unset", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.hunter }}
          >
            Mark all read
          </button>
        )}
      </div>
      {loading ? <div style={{ padding: 20 }}><Skeleton h={60} /></div> : items.length === 0 ? (
        <div style={{ padding: 20, fontSize: 12.5, color: C.muted }}>No notifications yet.</div>
      ) : (
        items.map((n) => {
          const unread = !n.read_at;
          const dest = routeFor(n.type, isSeller);
          const clickable = unread || !!dest;
          return (
            <div
              key={n.id}
              onClick={() => clickable && handleTap(n)}
              style={{
                padding: "14px 22px", borderBottom: `1px solid ${C.cream}`,
                display: "flex", alignItems: "flex-start", gap: 12,
                cursor: clickable ? "pointer" : "default",
                background: unread ? C.cream : "transparent",
                transition: "background 0.15s",
              }}
            >
              <span style={{ width: 30, height: 30, borderRadius: 8, background: unread ? C.hunter : C.dustDk, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={NOTIF_ICONS[n.type] || "bell"} size={13} color={C.dust} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>{n.title}</div>
                <div style={{ fontSize: 12, color: C.slate, marginTop: 2 }}>{n.body}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{timeAgo(n.created_at)}</div>
              </div>
              {unread && <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.hunter, marginTop: 6 }} />}
            </div>
          );
        })
      )}
    </Card>
  );
}

